import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, useMediaQuery } from '@mui/material';
import { lightTheme, darkTheme } from './theme';
import { useLocation } from 'react-router-dom';
import { BOOKING_APPEARANCE_KEY, normalizeBookingAppearance, isPublicBookingPath, resolveBookingMode } from './bookingAppearance';
import './index.css'; 

const ThemeContext = createContext();

export const ThemeProviderComponent = ({ children }) => {
    const [mode, setMode] = useState(() => {
        try { return localStorage.getItem('timely-appearance') === 'dark' ? 'dark' : 'light'; }
        catch { return 'light'; }
    });
    const { pathname } = useLocation();
    const isBookingAppearance = isPublicBookingPath(pathname);
    const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
    const [bookingAppearance, updateBookingAppearance] = useState(() => {
        try { return normalizeBookingAppearance(sessionStorage.getItem(BOOKING_APPEARANCE_KEY)); }
        catch { return 'system'; }
    });
    const setBookingAppearance = value => {
        const next = normalizeBookingAppearance(value);
        updateBookingAppearance(next);
        try { sessionStorage.setItem(BOOKING_APPEARANCE_KEY, next); } catch { /* Keep working without storage. */ }
    };
    const effectiveMode = isBookingAppearance ? resolveBookingMode(bookingAppearance, systemDark) : mode;
    const theme = effectiveMode === 'dark' ? darkTheme : lightTheme;
    const setAppearance = (nextMode) => {
        if (nextMode === 'light' || nextMode === 'dark') setMode(nextMode);
    };
    const toggleTheme = () => setMode(previous => previous === 'light' ? 'dark' : 'light');
    useEffect(() => {
        try { localStorage.setItem('timely-appearance', mode); } catch { /* Storage may be unavailable. */ }
    }, [mode]);

    return (
        <ThemeContext.Provider value={{ toggleTheme, mode: effectiveMode, setAppearance, isBookingAppearance, bookingAppearance, setBookingAppearance }}>
            <ThemeProvider theme={theme}>
                <CssBaseline /> {/* Applies consistent global styles */}
                <Box
                    sx={{
                        background: theme.customSurfaces?.pageBackgroundGradient || theme.palette.background.default,
                        color: theme.palette.text.primary,
                        minHeight: '100vh', // Ensure the container covers the full height of the viewport
                        overflowX: 'hidden', 
                        fontWeight: theme.typography.fontWeightRegular, // Applies the theme's regular font weight 
                    }}
                >
                    {children}
                </Box>

            </ThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
